import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Section, SectionHeader } from "@/components/Section";
import { FAQ } from "@/components/FAQ";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Reveal } from "@/components/Reveal";
import { Tilt3D } from "@/components/Tilt3D";
import { SITE } from "@/lib/site";

const CANONICAL = "https://www.svgmultimidia.com.br/crm-para-whatsapp";

const AI_WHATSAPP = "https://pixel.agendasistemacrm.com.br/r/qxgushz";

function WhatsIcon({ className = "" }: { className?: string }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden className={className}>
      <path d="M20.52 3.48A11.78 11.78 0 0 0 12.04 0C5.5 0 .19 5.3.19 11.84c0 2.09.55 4.13 1.6 5.93L0 24l6.4-1.68a11.83 11.83 0 0 0 5.64 1.43h.01c6.53 0 11.84-5.3 11.84-11.84 0-3.16-1.23-6.13-3.37-8.43Zm-3.06 11.86c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51l-.57-.01c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.49 0 1.47 1.07 2.89 1.22 3.09.15.2 2.1 3.2 5.08 4.49.71.31 1.27.5 1.7.64.71.23 1.36.2 1.87.12.57-.08 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35Z" />
    </svg>
  );
}

function CtaAI({
  children,
  size = "md",
  className = "",
}: {
  children: React.ReactNode;
  size?: "md" | "lg";
  className?: string;
}) {
  const sizes = size === "lg" ? "px-7 py-4 text-base" : "px-6 py-3.5 text-sm";
  return (
    <a
      href={AI_WHATSAPP}
      target="_blank"
      rel="noopener"
      className={`group relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-full font-semibold tracking-tight text-white shadow-[0_16px_44px_-14px_rgba(37,211,102,0.75)] ring-1 ring-ink/15 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_22px_58px_-14px_rgba(37,211,102,0.95)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-ring)] ${sizes} ${className}`}
      style={{ background: "linear-gradient(135deg,#22c55e 0%,#16a34a 55%,#15803d 100%)" }}
    >
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 group-hover:translate-x-full"
      />
      <WhatsIcon />
      <span className="relative">{children}</span>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden className="relative transition-transform duration-300 group-hover:translate-x-1">
        <path d="M5 12h14" />
        <path d="m13 5 7 7-7 7" />
      </svg>
    </a>
  );
}

const TOOLS: Array<{ title: string; desc: string; icon: React.ReactNode }> = [
  {
    title: "IA de atendimento 24/7",
    desc: "Inteligência artificial responde, qualifica e conduz o cliente pelo WhatsApp em qualquer horário — inclusive nos finais de semana.",
    icon: (
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M12 2a4 4 0 0 1 4 4v2h1a3 3 0 0 1 3 3v3a3 3 0 0 1-3 3h-1v1a4 4 0 0 1-4 4 4 4 0 0 1-4-4v-1H7a3 3 0 0 1-3-3v-3a3 3 0 0 1 3-3h1V6a4 4 0 0 1 4-4Z" />
        <circle cx="9.5" cy="12" r="1" />
        <circle cx="14.5" cy="12" r="1" />
      </svg>
    ),
  },
  {
    title: "WhatsApp para vários atendentes",
    desc: "Um único número, vários atendentes ao mesmo tempo. Cada conversa fica com o responsável certo, sem perder cliente e sem confusão.",
    icon: (
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M17 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" />
        <circle cx="10" cy="7" r="4" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M17 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
  {
    title: "Follow-up automático",
    desc: "Retomadas de conversa programadas para leads que sumiram, orçamentos parados e clientes prontos para comprar de novo.",
    icon: (
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M21 12a9 9 0 1 1-3.2-6.9" />
        <path d="M21 3v6h-6" />
      </svg>
    ),
  },
  {
    title: "Automação de atendimento",
    desc: "Fluxos, respostas rápidas, tags, funis e regras que padronizam a equipe e liberam horas do dia a dia comercial.",
    icon: (
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M12 20v-6" />
        <path d="M6 20v-4" />
        <path d="M18 20v-8" />
        <path d="M4 8h16" />
        <rect x="2" y="4" width="20" height="4" rx="1" />
      </svg>
    ),
  },
  {
    title: "CRM para equipe comercial",
    desc: "Kanban de negociações, histórico completo por cliente, notas, tarefas e visão clara de quem está vendendo mais.",
    icon: (
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <rect x="3" y="4" width="18" height="16" rx="2" />
        <path d="M8 8v8" />
        <path d="M13 8v5" />
        <path d="M18 8v3" />
      </svg>
    ),
  },
  {
    title: "Relatórios e produtividade",
    desc: "Painéis com volume de conversas, tempo de resposta, conversões e desempenho por atendente — sem planilhas.",
    icon: (
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M3 3v18h18" />
        <path d="M7 15l4-4 3 3 5-6" />
      </svg>
    ),
  },
  {
    title: "Integrações e API",
    desc: "Conecta com API oficial e não oficial do WhatsApp, sistemas internos, sites, formulários e ferramentas de marketing.",
    icon: (
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1" />
        <path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" />
      </svg>
    ),
  },
  {
    title: "Organização por setores",
    desc: "Distribua conversas por setor (comercial, financeiro, suporte) e mantenha o atendimento da empresa 100% organizado.",
    icon: (
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <rect x="3" y="3" width="7" height="7" rx="1.5" />
        <rect x="14" y="3" width="7" height="7" rx="1.5" />
        <rect x="3" y="14" width="7" height="7" rx="1.5" />
        <rect x="14" y="14" width="7" height="7" rx="1.5" />
      </svg>
    ),
  },
];

const STEPS = [
  {
    n: "01",
    title: "Você conversa com a IA",
    desc: "Clique no botão de WhatsApp e fale direto com a IA de atendimento do CRM SVG. Ela entende sua empresa e mostra como o sistema funciona.",
  },
  {
    n: "02",
    title: "Conectamos seu WhatsApp",
    desc: "Ativamos o CRM no seu número atual, com API oficial ou não oficial, e organizamos setores, atendentes e fluxos.",
  },
  {
    n: "03",
    title: "IA + equipe atendendo juntos",
    desc: "A inteligência artificial responde na hora, qualifica leads, agenda e passa para o atendente humano quando faz sentido.",
  },
  {
    n: "04",
    title: "Você vende mais, com menos esforço",
    desc: "Follow-ups automáticos, funis organizados e relatórios claros para a equipe comercial bater metas todo mês.",
  },
];

const FAQS = [
  {
    q: "O que é o CRM SVG com IA para WhatsApp?",
    a: "É um CRM com IA para WhatsApp que centraliza todo o atendimento da sua empresa em um só lugar. A inteligência artificial responde, qualifica e conduz o cliente, enquanto sua equipe assume nas conversas mais estratégicas.",
  },
  {
    q: "Funciona com WhatsApp para vários atendentes no mesmo número?",
    a: "Sim. É um sistema de atendimento WhatsApp para empresas: vários atendentes usam o mesmo número, com histórico, transferências, tags e distribuição por setor — sem depender de um único celular.",
  },
  {
    q: "A IA substitui a equipe?",
    a: "Não. A IA de atendimento assume o primeiro contato, tira dúvidas, agenda e organiza a informação. A equipe entra nas etapas em que a decisão de compra realmente acontece.",
  },
  {
    q: "Como funciona a automação de atendimento no WhatsApp?",
    a: "O CRM permite criar respostas rápidas, fluxos automáticos, follow-ups programados e regras de distribuição, tornando o atendimento previsível e escalável.",
  },
  {
    q: "Vocês oferecem integração com a API oficial do WhatsApp?",
    a: "Sim. Trabalhamos com API oficial e não oficial, escolhendo a melhor opção para o seu volume, tipo de negócio e estratégia.",
  },
  {
    q: "Serve para minha equipe comercial?",
    a: "Sim. O CRM SVG é pensado como CRM para equipe comercial, com kanban de negociações, tarefas, follow-ups e relatórios de desempenho por vendedor.",
  },
  {
    q: "Como faço para conhecer o sistema?",
    a: "Clique em qualquer botão desta página. Você é levado direto ao WhatsApp para conversar com a IA de atendimento do CRM SVG e tirar todas as suas dúvidas.",
  },
];

const BENEFITS_24H: Array<{ title: string; desc: string; stat: string; label: string }> = [
  {
    title: "Trabalha 24h, todos os dias",
    desc: "A IA responde no mesmo segundo, de madrugada, feriado ou domingo. Nenhum lead fica sem resposta enquanto sua equipe descansa.",
    stat: "24/7",
    label: "sem pausa, sem folga",
  },
  {
    title: "Não erra, não esquece, não desiste",
    desc: "Executa o mesmo padrão de atendimento em toda conversa e faz follow-up automático de quem sumiu, sem depender da memória de ninguém.",
    stat: "0",
    label: "leads esquecidos",
  },
  {
    title: "Custa uma fração de um atendente",
    desc: "Substitui o trabalho repetitivo de várias horas por dia por um custo fixo baixo — sem encargos, sem férias, sem rotatividade.",
    stat: "-70%",
    label: "de tempo operacional",
  },
  {
    title: "Libera sua equipe para vender",
    desc: "Enquanto a IA filtra dúvidas, qualifica e agenda, seus vendedores entram só nas conversas prontas para fechar — produtividade dispara.",
    stat: "+3x",
    label: "de foco no que vende",
  },
];

const VIDEO_TESTIMONIALS: Array<{ id: string; title: string }> = [
  { id: "qUtZ-7QeuGs", title: "Depoimento de cliente CRM SVG" },
  { id: "3x6b1MVOMZk", title: "Depoimento de cliente CRM SVG" },
  { id: "QKJA4wfRECk", title: "Depoimento de cliente CRM SVG" },
];

const WRITTEN_TESTIMONIALS: Array<{ name: string; role: string; content: string }> = [
  { name: "Rafael Menezes", role: "Diretor comercial · Imobiliária", content: "Em 30 dias o CRM SVG dobrou nosso volume de atendimentos sem contratar ninguém. A IA responde na hora, qualifica e ainda agenda visita. Vendas subiram 47% no segundo mês." },
  { name: "Camila Ferreira", role: "Sócia · Clínica de estética", content: "Antes a gente perdia lead por demorar a responder no WhatsApp. Hoje a IA atende em segundos, agenda sozinha e ainda manda lembrete. Reduzimos faltas em 60% e a agenda vive cheia." },
  { name: "Diego Almeida", role: "CEO · E-commerce", content: "Ganhei pelo menos 4 horas por dia que eu gastava respondendo cliente. A IA fecha venda simples sozinha e só me chama quando é caso grande. Produtividade da equipe explodiu." },
  { name: "Patrícia Lopes", role: "Gestora · Escola de idiomas", content: "O follow-up automático recuperou matrículas que estavam paradas há meses. Em uma campanha só, fechamos 28 alunos vindos de leads frios que a IA reativou." },
  { name: "Lucas Bertoldi", role: "Diretor · Concessionária", content: "Implantamos o CRM SVG e em 60 dias o time vendeu 35% a mais com a mesma equipe. A IA filtra curioso e entrega o lead quente pronto pro vendedor fechar." },
  { name: "Juliana Reis", role: "Proprietária · Salão de beleza", content: "A agenda lota sozinha. A IA conversa igualzinho a gente, manda foto, tira dúvida e marca. Eu só apareço pra atender. Nunca tive tanto tempo livre nem tanto faturamento." },
  { name: "Marcos Vinicius", role: "Sócio · Construtora", content: "O painel mostra tudo: quantos leads vieram, quantos a IA fechou, quanto custou cada venda. Tomamos decisão com dado, não no achismo. Faturamento mensal cresceu 52%." },
  { name: "Aline Castro", role: "Head de marketing · SaaS B2B", content: "Conectamos o CRM SVG aos anúncios da Meta e a IA passou a qualificar lead 24/7. Custo por reunião agendada caiu pela metade e o time de vendas só fala com lead pronto." },
];

function formatBRL(v: number): string {
  if (!Number.isFinite(v)) return "R$ 0";
  return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
}

function formatHours(v: number): string {
  if (!Number.isFinite(v) || v <= 0) return "0h";
  if (v < 1) return `${Math.round(v * 60)} min`;
  const h = Math.floor(v);
  const m = Math.round((v - h) * 60);
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
}

function CalcField({
  label,
  suffix,
  value,
  min,
  max,
  step = 1,
  onChange,
}: {
  label: string;
  suffix?: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (n: number) => void;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <label className="text-sm font-medium text-foreground/90">{label}</label>
        <span className="font-display text-lg font-semibold text-primary">
          {value.toLocaleString("pt-BR")}
          {suffix ? <span className="ml-1 text-xs text-muted-foreground">{suffix}</span> : null}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="mt-2 h-2 w-full cursor-pointer appearance-none rounded-full bg-ink/[0.08] accent-[color:var(--color-primary)]"
      />
      <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
        <span>{min.toLocaleString("pt-BR")}</span>
        <span>{max.toLocaleString("pt-BR")}</span>
      </div>
    </div>
  );
}

function ProductivityCalculator() {
  const [leadsPerDay, setLeadsPerDay] = useState<number>(40);
  const [salespeople, setSalespeople] = useState<number>(3);
  const [avgMinutes, setAvgMinutes] = useState<number>(8);
  const [ticket, setTicket] = useState<number>(600);
  const [conversion, setConversion] = useState<number>(15);

  const result = useMemo(() => {
    // IA assume ~70% do tempo de triagem/dúvidas iniciais e follow-up.
    const aiHandledShare = 0.7;
    const workDaysMonth = 22;
    const daysMonth = 30;

    const totalMinutesPerDay = leadsPerDay * avgMinutes;
    const savedMinutesPerDay = totalMinutesPerDay * aiHandledShare;
    const savedHoursPerDay = savedMinutesPerDay / 60;
    const savedHoursPerMonth = savedHoursPerDay * workDaysMonth;
    const savedHoursPerSalesperson = salespeople > 0 ? savedHoursPerMonth / salespeople : savedHoursPerMonth;

    // Leads adicionais atendidos por operar fora do horário comercial (~16h/dia + fins de semana).
    // Estimativa: 35% dos leads chegam fora de horário e hoje ficam sem resposta rápida.
    const outOfHoursShare = 0.35;
    const extraLeadsMonth = leadsPerDay * outOfHoursShare * daysMonth;

    // Uplift adicional de conversão em leads já atendidos, por resposta imediata + follow-up.
    // +25% de conversão sobre o volume que sua equipe já atende.
    const attendedLeadsMonth = leadsPerDay * workDaysMonth;
    const baseSales = attendedLeadsMonth * (conversion / 100);
    const upliftSales = baseSales * 0.25;

    // Vendas extras vindas dos leads fora de horário resgatados.
    const rescuedSales = extraLeadsMonth * (conversion / 100);

    const extraSalesMonth = upliftSales + rescuedSales;
    const extraRevenueMonth = extraSalesMonth * ticket;

    return {
      savedHoursPerDay,
      savedHoursPerMonth,
      savedHoursPerSalesperson,
      extraLeadsMonth,
      extraSalesMonth,
      extraRevenueMonth,
    };
  }, [leadsPerDay, salespeople, avgMinutes, ticket, conversion]);

  return (
    <div className="grid gap-6 lg:grid-cols-[1.05fr_1fr]">
      {/* Inputs */}
      <div className="card-glass p-6 md:p-8">
        <div className="flex items-center gap-2">
          <span className="grid h-9 w-9 place-items-center rounded-xl bg-primary/15 text-primary ring-1 ring-primary/30">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M4 4h16v4H4z" />
              <path d="M4 12h10v8H4z" />
              <path d="M16 12h4v4h-4z" />
              <path d="M16 20h4" />
            </svg>
          </span>
          <h3 className="font-display text-lg font-semibold">Seus números hoje</h3>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">Ajuste os controles e veja a estimativa em tempo real.</p>

        <div className="mt-6 grid gap-6 sm:grid-cols-2">
          <CalcField label="Leads por dia" suffix="leads" value={leadsPerDay} min={5} max={500} onChange={setLeadsPerDay} />
          <CalcField label="Vendedores na equipe" suffix="pessoas" value={salespeople} min={1} max={30} onChange={setSalespeople} />
          <CalcField label="Tempo médio por lead" suffix="min" value={avgMinutes} min={2} max={45} onChange={setAvgMinutes} />
          <CalcField label="Ticket médio" suffix="R$" value={ticket} min={50} max={10000} step={50} onChange={setTicket} />
          <div className="sm:col-span-2">
            <CalcField label="Taxa de conversão atual" suffix="%" value={conversion} min={1} max={80} onChange={setConversion} />
          </div>
        </div>

        <p className="mt-6 text-[11px] leading-relaxed text-muted-foreground">
          Estimativa baseada em: IA absorvendo ~70% do atendimento inicial e follow-up, ~35% dos leads chegando fora do horário comercial e uplift de ~25% na conversão por resposta imediata e follow-up automático.
        </p>
      </div>

      {/* Resultados */}
      <div className="relative overflow-hidden rounded-3xl border border-ink/12 shadow-[var(--shadow-card)] bg-gradient-to-br from-white via-[oklch(0.985_0.006_258)] to-[oklch(0.965_0.015_160)] p-6 md:p-8">
        <div
          aria-hidden
          className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full opacity-60 blur-3xl"
          style={{ background: "radial-gradient(circle, rgba(34,197,94,0.35) 0%, transparent 70%)" }}
        />
        <div className="relative flex items-center gap-2">
          <span className="grid h-9 w-9 place-items-center rounded-xl bg-emerald-100 text-emerald-700 ring-1 ring-emerald-600/30">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M3 3v18h18" />
              <path d="M7 15l4-4 3 3 5-6" />
            </svg>
          </span>
          <h3 className="font-display text-lg font-semibold">Ganho estimado com o CRM SVG</h3>
        </div>
        <p className="relative mt-1 text-sm text-muted-foreground">Atualiza em tempo real conforme você move os controles.</p>

        <div className="relative mt-6 space-y-3">
          <div className="rounded-2xl border border-emerald-600/25 bg-emerald-50 p-5 shadow-[var(--shadow-soft)]">
            <div className="text-xs uppercase tracking-wider text-emerald-800">Faturamento extra estimado / mês</div>
            <div className="mt-1 font-display text-4xl font-bold text-emerald-700 md:text-5xl">
              {formatBRL(result.extraRevenueMonth)}
            </div>
            <div className="mt-1 text-xs text-emerald-800">
              +{Math.round(result.extraSalesMonth).toLocaleString("pt-BR")} vendas adicionais por mês
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-2xl border border-ink/10 shadow-[var(--shadow-soft)] bg-ink/[0.04] p-4">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Tempo economizado / dia</div>
              <div className="mt-1 font-display text-2xl font-bold">{formatHours(result.savedHoursPerDay)}</div>
            </div>
            <div className="rounded-2xl border border-ink/10 shadow-[var(--shadow-soft)] bg-ink/[0.04] p-4">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Tempo economizado / mês</div>
              <div className="mt-1 font-display text-2xl font-bold">{formatHours(result.savedHoursPerMonth)}</div>
            </div>
            <div className="rounded-2xl border border-ink/10 shadow-[var(--shadow-soft)] bg-ink/[0.04] p-4">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Por vendedor / mês</div>
              <div className="mt-1 font-display text-2xl font-bold">{formatHours(result.savedHoursPerSalesperson)}</div>
            </div>
            <div className="rounded-2xl border border-ink/10 shadow-[var(--shadow-soft)] bg-ink/[0.04] p-4">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Leads resgatados fora de horário / mês</div>
              <div className="mt-1 font-display text-2xl font-bold">
                +{Math.round(result.extraLeadsMonth).toLocaleString("pt-BR")}
              </div>
            </div>
          </div>

          <div className="pt-2">
            <CtaAI className="w-full">Quero esses resultados no meu WhatsApp</CtaAI>
          </div>
        </div>
      </div>
    </div>
  );
}

function CrmParaWhatsappPage() {
  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-ink/12">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-70"
          style={{
            background:
              "radial-gradient(60% 50% at 20% 10%, oklch(0.66 0.22 258 / 0.25) 0%, transparent 70%), radial-gradient(50% 40% at 90% 20%, rgba(34,197,94,0.18) 0%, transparent 70%)",
          }}
        />
        <div className="container-svg relative py-16 md:py-24">
          <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "CRM para WhatsApp" }]} />
          <div className="mt-6 grid items-center gap-10 lg:grid-cols-[1.1fr_1fr]">
            <Reveal>
              <span className="inline-flex items-center gap-2 rounded-full border border-ink/10 bg-ink/[0.04] px-3 py-1 text-xs font-medium text-muted-foreground">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_12px_var(--tw-shadow-color)] shadow-emerald-500/70" />
                CRM com IA para WhatsApp
              </span>
              <h1 className="mt-4 font-display text-4xl font-bold leading-[1.05] tracking-tight md:text-6xl">
                Atenda, venda e faça <span className="text-gradient">follow-up</span> no WhatsApp com <span className="text-primary">inteligência artificial</span>
              </h1>
              <p className="mt-5 max-w-xl text-base text-muted-foreground md:text-lg">
                O CRM SVG organiza o atendimento da sua empresa no WhatsApp, coloca vários atendentes no mesmo número e ativa uma IA que responde, qualifica e agenda para você 24 horas por dia.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <CtaAI size="lg">Falar com a IA agora</CtaAI>
                <a
                  href="#como-funciona"
                  className="inline-flex items-center gap-2 rounded-full border border-ink/15 bg-ink/[0.04] px-5 py-3 text-sm font-medium text-foreground/90 transition hover:bg-ink/[0.08]"
                >
                  Ver como funciona
                </a>
              </div>
              <ul className="mt-8 grid max-w-lg gap-2 text-sm text-muted-foreground sm:grid-cols-2">
                {["Vários atendentes no mesmo número", "IA responde em segundos", "Follow-up automático", "API oficial e não oficial"].map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 text-emerald-400" aria-hidden>
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal delay={2}>
              <Tilt3D max={8}>
                <div className="card-glass relative overflow-hidden rounded-3xl p-1">
                  <div className="rounded-[calc(theme(borderRadius.3xl)-4px)] bg-gradient-to-br from-[oklch(0.985_0.005_258)] to-[oklch(0.955_0.012_160)] p-5 text-foreground">
                    <div className="flex items-center justify-between border-b border-ink/12 pb-3">
                      <div className="flex items-center gap-2">
                        <div className="grid h-9 w-9 place-items-center rounded-full bg-emerald-600 text-white shadow-[var(--shadow-soft)]">
                          <WhatsIcon />
                        </div>
                        <div className="leading-tight">
                          <div className="text-sm font-semibold">IA de atendimento</div>
                          <div className="text-[11px] font-medium text-emerald-700">online agora</div>
                        </div>
                      </div>
                      <span className="rounded-full border border-ink/12 bg-white px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground shadow-[var(--shadow-soft)]">CRM SVG</span>
                    </div>
                    <div className="mt-4 space-y-3 text-sm">
                      <div className="max-w-[85%] rounded-2xl rounded-tl-sm border border-ink/10 bg-white px-3 py-2 shadow-[var(--shadow-soft)]">
                        Oi! 👋 Vi que você quer conhecer o CRM SVG. Qual é o setor da sua empresa?
                      </div>
                      <div className="ml-auto max-w-[80%] rounded-2xl rounded-tr-sm bg-emerald-600 px-3 py-2 text-white shadow-[var(--shadow-soft)]">
                        Sou de uma clínica e recebo muita mensagem no WhatsApp.
                      </div>
                      <div className="max-w-[90%] rounded-2xl rounded-tl-sm border border-ink/10 bg-white px-3 py-2 shadow-[var(--shadow-soft)]">
                        Perfeito. Consigo organizar tudo em um só número, com vários atendentes, IA respondendo e follow-up automático para pacientes que não confirmaram. Quer ver como fica no seu caso?
                      </div>
                      <div className="ml-auto flex max-w-[70%] items-center gap-2 rounded-2xl rounded-tr-sm bg-emerald-600 px-3 py-2 text-white shadow-[var(--shadow-soft)]">
                        <span className="inline-flex h-2 w-2 animate-pulse rounded-full bg-emerald-100" />
                        digitando...
                      </div>
                    </div>
                    <div className="mt-5">
                      <CtaAI className="w-full">Continuar essa conversa</CtaAI>
                    </div>
                  </div>
                </div>
              </Tilt3D>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PROVA SOCIAL — USUÁRIOS ATIVOS */}
      <section className="border-b border-ink/12 bg-[color:var(--color-sidebar)]/60">
        <div className="container-svg py-8 md:py-10">
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4 text-center">
            <div className="flex items-center gap-3">
              <span className="grid h-11 w-11 place-items-center rounded-2xl bg-emerald-500/15 text-emerald-700 ring-1 ring-emerald-400/40">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M17 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" />
                  <circle cx="10" cy="7" r="4" />
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M17 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </span>
              <div className="text-left">
                <div className="font-display text-2xl font-bold leading-none md:text-3xl">
                  +3.500 <span className="text-primary">usuários ativos</span>
                </div>
                <div className="mt-1 text-xs text-muted-foreground">Empresas atendendo no WhatsApp com o CRM SVG hoje</div>
              </div>
            </div>
            <div className="hidden h-10 w-px bg-ink/10 md:block" />
            <div className="text-sm text-muted-foreground">
              IA de atendimento ativa <span className="text-foreground font-semibold">24 horas por dia</span>, 7 dias por semana
            </div>
          </div>
        </div>
      </section>

      {/* FERRAMENTAS */}
      <Section>
        <SectionHeader
          eyebrow="Ferramentas do CRM SVG"
          title={<>Tudo que sua empresa precisa para <span className="text-primary">atender pelo WhatsApp</span></>}
          description="Um sistema de atendimento WhatsApp para empresas, com CRM, automações e IA integrados em uma única plataforma."
        />
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {TOOLS.map((t, i) => (
            <Reveal key={t.title} delay={((i % 3) + 1) as 1 | 2 | 3}>
              <div className="card-glass group relative h-full overflow-hidden p-5 transition hover:-translate-y-1">
                <div
                  aria-hidden
                  className="pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full opacity-60 blur-2xl transition group-hover:opacity-90"
                  style={{ background: "radial-gradient(circle, oklch(0.66 0.22 258 / 0.35) 0%, transparent 70%)" }}
                />
                <div className="relative grid h-11 w-11 place-items-center rounded-xl bg-primary/15 text-primary ring-1 ring-primary/30">
                  {t.icon}
                </div>
                <h3 className="relative mt-4 font-display text-lg font-semibold">{t.title}</h3>
                <p className="relative mt-1.5 text-sm text-muted-foreground">{t.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-10 flex justify-center">
          <CtaAI>Quero ver isso no meu WhatsApp</CtaAI>
        </div>
      </Section>

      {/* COMO FUNCIONA */}
      <Section id="como-funciona" className="bg-[color:var(--color-sidebar)]">
        <SectionHeader
          eyebrow="Como funciona"
          title={<>Da primeira conversa ao <span className="text-primary">cliente atendido</span></>}
          description="Simples, visual e sem enrolação: veja o caminho para organizar e automatizar o atendimento da sua empresa no WhatsApp."
        />
        <div className="relative mt-12">
          <div
            aria-hidden
            className="absolute left-1/2 top-0 hidden h-full w-px -translate-x-1/2 bg-gradient-to-b from-transparent via-primary/40 to-transparent lg:block"
          />
          <ol className="grid gap-6 lg:grid-cols-2">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={((i % 3) + 1) as 1 | 2 | 3}>
                <li className={`card-glass relative p-6 lg:${i % 2 === 0 ? "mr-6" : "ml-6"}`}>
                  <div className="flex items-center gap-3">
                    <span
                      className="grid h-11 w-11 place-items-center rounded-2xl font-display text-lg font-bold text-white shadow-[0_10px_30px_-10px_oklch(0.62_0.22_258/0.7)]"
                      style={{ background: "linear-gradient(135deg,oklch(0.66 0.22 258),oklch(0.55 0.22 268))" }}
                    >
                      {s.n}
                    </span>
                    <h3 className="font-display text-xl font-semibold">{s.title}</h3>
                  </div>
                  <p className="mt-3 text-sm text-muted-foreground">{s.desc}</p>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
        <div className="mt-10 flex justify-center">
          <CtaAI size="lg">Começar pela conversa com a IA</CtaAI>
        </div>
      </Section>

      {/* FUNCIONÁRIO 24H */}
      <Section>
        <SectionHeader
          eyebrow="Um funcionário que nunca dorme"
          title={<>A IA do CRM SVG trabalha <span className="text-primary">24 horas por dia</span> pela sua empresa</>}
          description="Ela atende, qualifica e faz follow-up sem parar — o que sua equipe demora horas para fazer, a IA resolve em segundos, todos os dias, inclusive de madrugada e no domingo."
        />
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {BENEFITS_24H.map((b, i) => (
            <Reveal key={b.title} delay={((i % 3) + 1) as 1 | 2 | 3}>
              <div className="card-glass relative h-full overflow-hidden p-6">
                <div
                  aria-hidden
                  className="pointer-events-none absolute -right-10 -bottom-10 h-28 w-28 rounded-full opacity-50 blur-2xl"
                  style={{ background: "radial-gradient(circle, rgba(34,197,94,0.35) 0%, transparent 70%)" }}
                />
                <div className="relative font-display text-4xl font-bold text-emerald-700 md:text-5xl">{b.stat}</div>
                <div className="relative mt-1 text-[11px] uppercase tracking-wider text-muted-foreground">{b.label}</div>
                <h3 className="relative mt-4 font-display text-lg font-semibold">{b.title}</h3>
                <p className="relative mt-1.5 text-sm text-muted-foreground">{b.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-8 grid gap-4 md:grid-cols-3">
          {[
            { t: "Manhã", d: "Enquanto a equipe abre a loja, a IA já respondeu 20 conversas da madrugada e agendou reuniões." },
            { t: "Tarde", d: "Filtra dúvidas simples, orça, tira objeções e passa para o vendedor só quem está pronto para fechar." },
            { t: "Noite e fim de semana", d: "A IA continua atendendo, não deixa nenhum lead sem resposta, mantém o follow-up ativo e sua empresa presente no WhatsApp mesmo com a equipe fora do expediente." },
          ].map((slot) => (
            <div key={slot.t} className="rounded-2xl border border-ink/10 shadow-[var(--shadow-soft)] bg-ink/[0.045] p-5">
              <div className="text-xs uppercase tracking-wider text-primary/80">{slot.t}</div>
              <p className="mt-1 text-sm text-muted-foreground">{slot.d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* CALCULADORA DE PRODUTIVIDADE */}
      <Section id="calculadora" className="bg-[color:var(--color-sidebar)]">
        <SectionHeader
          eyebrow="Calculadora de produtividade"
          title={<>Quanto tempo e <span className="text-primary">quanto faturamento</span> a IA pode gerar na sua empresa</>}
          description="Informe o volume de leads, o tamanho da equipe e o ticket médio. A calculadora mostra em tempo real o tempo economizado e o faturamento adicional estimado com o CRM SVG."
        />
        <div className="mt-12">
          <ProductivityCalculator />
        </div>
      </Section>

      {/* VÍDEO */}
      <Section>
        <SectionHeader
          eyebrow="Demonstração"
          title={<>Veja o CRM SVG <span className="text-primary">atendendo no WhatsApp</span></>}
          description="Uma visão real de como a IA conduz conversas, qualifica leads e organiza o dia a dia da equipe."
        />
        <Reveal>
          <div className="mx-auto mt-10 max-w-4xl">
            <div className="relative aspect-video overflow-hidden rounded-3xl border border-ink/10 shadow-[0_30px_80px_-30px_oklch(0_0_0_/_0.8)]">
              <iframe
                src="https://www.youtube.com/embed/hekqf2hE_mI"
                title="Demonstração do CRM SVG com IA para WhatsApp"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                loading="lazy"
                className="absolute inset-0 h-full w-full"
              />
            </div>
          </div>
        </Reveal>
        <div className="mt-10 flex justify-center">
          <CtaAI>Quero conversar com a IA de atendimento</CtaAI>
        </div>
      </Section>

      {/* CTA FINAL */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(50% 60% at 20% 30%, rgba(34,197,94,0.18) 0%, transparent 70%), radial-gradient(50% 50% at 80% 60%, oklch(0.62 0.22 258 / 0.22) 0%, transparent 70%)",
          }}
        />
        <div className="container-svg relative py-16 md:py-24">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="font-display text-3xl font-bold tracking-tight md:text-5xl">
              Fale agora com a <span className="text-primary">IA de atendimento</span> do CRM SVG
            </h2>
            <p className="mt-4 text-muted-foreground md:text-lg">
              A melhor forma de conhecer o sistema é conversando com a IA que atende dentro dele. Em poucos minutos você vê, na prática, como ela poderia estar atendendo seus clientes.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <CtaAI size="lg">Conversar com a IA no WhatsApp</CtaAI>
            </div>
            <p className="mt-4 text-xs text-muted-foreground">Atendimento humano disponível na mesma conversa.</p>
          </div>
        </div>
      </section>

      {/* DEPOIMENTOS */}
      <Section>
        <SectionHeader
          eyebrow="Depoimentos"
          title={<>Clientes vendendo mais com o <span className="text-primary">CRM SVG</span></>}
          description="Quem ativou a IA no WhatsApp ganhou tempo, produtividade e faturamento. Veja relatos reais de empresas usando o CRM SVG."
        />

        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {VIDEO_TESTIMONIALS.map((v, i) => (
            <Reveal key={v.id} delay={((i % 3) + 1) as 1 | 2 | 3}>
              <Tilt3D max={6}>
                <div className="card-glass overflow-hidden p-2">
                  <div className="relative aspect-[9/16] overflow-hidden rounded-2xl border border-ink/10 shadow-[var(--shadow-soft)]">
                    <iframe
                      src={`https://www.youtube.com/embed/${v.id}`}
                      title={v.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      loading="lazy"
                      className="absolute inset-0 h-full w-full"
                    />
                  </div>
                </div>
              </Tilt3D>
            </Reveal>
          ))}
        </div>

        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {WRITTEN_TESTIMONIALS.map((t, i) => (
            <Reveal key={t.name} delay={((i % 4) + 1) as 1 | 2 | 3 | 4}>
              <Tilt3D max={5}>
                <figure className="card-glass flex h-full flex-col">
                  <div className="flex gap-0.5" aria-label="5 de 5 estrelas">
                    {Array.from({ length: 5 }).map((_, k) => (
                      <svg key={k} width="14" height="14" viewBox="0 0 24 24" fill="#FBBC04" stroke="#FBBC04" strokeWidth="1.5" aria-hidden>
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                      </svg>
                    ))}
                  </div>
                  <blockquote className="mt-3 flex-1 text-sm leading-relaxed">“{t.content}”</blockquote>
                  <figcaption className="mt-4 border-t border-border pt-3">
                    <p className="text-sm font-semibold">{t.name}</p>
                    <p className="text-xs text-muted-foreground">{t.role}</p>
                  </figcaption>
                </figure>
              </Tilt3D>
            </Reveal>
          ))}
        </div>

        <div className="mt-10 flex justify-center">
          <CtaAI>Quero esses resultados na minha empresa</CtaAI>
        </div>
      </Section>

      {/* FAQ */}
      <Section className="bg-[color:var(--color-sidebar)]">
        <SectionHeader
          eyebrow="Perguntas frequentes"
          title={<>Tire suas dúvidas sobre <span className="text-primary">CRM com IA para WhatsApp</span></>}
        />
        <div className="mx-auto mt-10 max-w-3xl">
          <FAQ items={FAQS} />
        </div>
        <div className="mt-10 flex justify-center">
          <CtaAI>Ainda tenho dúvidas — falar com a IA</CtaAI>
        </div>
      </Section>
    </>
  );
}

export const Route = createFileRoute("/crm-para-whatsapp")({
  head: () => ({
    meta: [
      { title: "CRM com IA para WhatsApp | Sistema de Atendimento WhatsApp para Empresas — SVG" },
      {
        name: "description",
        content:
          "CRM com IA para WhatsApp: sistema de atendimento WhatsApp para empresas, com vários atendentes no mesmo número, automação, follow-up e IA de atendimento 24/7. Fale agora com a IA.",
      },
      {
        name: "keywords",
        content:
          "CRM com IA para WhatsApp, sistema de atendimento WhatsApp para empresas, WhatsApp para vários atendentes, organizar atendimento da empresa no WhatsApp, automatizar atendimento no WhatsApp, sistema de follow-up pelo WhatsApp, CRM para equipe comercial, atendimento automático com inteligência artificial",
      },
      { property: "og:title", content: "CRM com IA para WhatsApp — Atendimento automático para empresas" },
      {
        property: "og:description",
        content:
          "Organize e automatize o atendimento da sua empresa no WhatsApp com o CRM SVG. Vários atendentes, IA 24/7, follow-up e integração com API oficial.",
      },
      { property: "og:type", content: "product" },
      { property: "og:url", content: CANONICAL },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          name: "CRM SVG com IA para WhatsApp",
          brand: { "@type": "Brand", name: "SVG Multimídia" },
          description:
            "Sistema de atendimento WhatsApp para empresas com IA, vários atendentes no mesmo número, automação, follow-up e CRM para equipe comercial.",
          url: CANONICAL,
          category: "Business Software",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: CrmParaWhatsappPage,
});